import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { FieldErrors, Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useWarehouses } from '@/hooks/useWarehouses';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { IReqCreateArrival } from '@/types/fulfill/inventory/createArrival.interface';
import { formatDate } from '@/utils/common';
import { translateErrorMessage } from '@/utils/errorTranslations';
import { extractErrorInfo } from '@/utils/httpUtils';

import {
  COUNTRY_DIAL_CODES,
  COUNTRY_TO_PHONE_CODE,
  CountryCode,
  DEFAULT_COUNTRY,
  MAX_PHONE_LENGTH_BY_COUNTRY,
  PickupData,
  SendToWarehouseFormData,
  sendToWarehouseSchema,
} from '../inventorySchema';
import { PACKAGE_SIZES, pickupCountryRequiresZipcode } from './dependencies/pickupConstants';

interface EditProducts {
  index: number;
  sku: string;
  quantity: string;
}

interface UseSendToWarehouse {
  onClose: () => void;
  products: ProductResponse['products'];
}

const useSendToWarehouse = ({ onClose, products }: UseSendToWarehouse) => {
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const userCountry = profile?.data?.country || 'MEX';
  const initialCountryCode = COUNTRY_TO_PHONE_CODE[userCountry] || DEFAULT_COUNTRY;
  const [isPending, startTransition] = useTransition();
  const [currentStep, setCurrentStep] = useState(1);
  const [editStep, setEditStep] = useState(false);
  const [countryCode, setCountryCode] = useState<CountryCode>(initialCountryCode);
  const [warehouse, setWarehouse] = useState('');
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const { warehouseOptions, warehouses, isLoading: isLoadingWarehouses } = useWarehouses();
  const selectedWarehouse = warehouses.find((wh) => wh._id === warehouse);
  const http = initializeHttpProvider();

  const COUNTRY_CONFIG: Record<string, { code: string; display: string }> = {
    MEX: { code: 'MEX', display: 'México' },
    MX: { code: 'MEX', display: 'México' },
    PE: { code: 'PER', display: 'Perú' },
    PER: { code: 'PER', display: 'Perú' },
    CO: { code: 'COL', display: 'Colombia' },
    COL: { code: 'COL', display: 'Colombia' },
    CL: { code: 'CHL', display: 'Chile' },
    CHL: { code: 'CHL', display: 'Chile' },
  };

  const countryInfo = COUNTRY_CONFIG[userCountry] || { code: userCountry, display: userCountry };

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    control,
    setValue,
    getValues,
    watch,
  } = useForm<SendToWarehouseFormData>({
    resolver: yupResolver(sendToWarehouseSchema) as unknown as Resolver<SendToWarehouseFormData>,
    mode: 'onBlur',
    context: { countryCode },
    defaultValues: {
      products: [],
      appointment: null,
      requiresHomePickup: false,
      pickup: {
        packageSize: 'M',
        address: '',
        city: '',
        country: countryInfo.code,
        postalCode: '',
        reference: '',
        notes: '',
      },
      parcels: '',
      contactPhone: COUNTRY_DIAL_CODES[initialCountryCode],
      shipmentType: 'SPT',
      warehouse: '',
    },
  });

  useEffect(() => {
    if (countryInfo.code) {
      setValue('pickup.country', countryInfo.code);
    }
  }, [countryInfo.code, setValue]);

  useEffect(() => {
    if (!profile?.data?.country) return;

    const phoneCountry = COUNTRY_TO_PHONE_CODE[profile.data.country] || DEFAULT_COUNTRY;
    setCountryCode(phoneCountry);

    const currentPhone = getValues('contactPhone');
    const isStillInitialDialCode = Object.values(COUNTRY_DIAL_CODES).includes(currentPhone);

    if (isStillInitialDialCode || !currentPhone) {
      setValue('contactPhone', COUNTRY_DIAL_CODES[phoneCountry]);
    }
  }, [profile?.data?.country]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profile?.data?.country) return;

    if (isLoadingWarehouses || warehouses.length === 0) return;

    const warehouseCountry = warehouses[0].location.country;
    const phoneCountry = COUNTRY_TO_PHONE_CODE[warehouseCountry] || DEFAULT_COUNTRY;

    if (phoneCountry === countryCode) return;

    setCountryCode(phoneCountry);

    // Solo actualizar el teléfono si el usuario no ha ingresado nada aún
    // (el valor sigue siendo uno de los prefijos iniciales)
    const currentPhone = getValues('contactPhone');
    const isStillInitialDialCode = Object.values(COUNTRY_DIAL_CODES).includes(currentPhone);

    if (isStillInitialDialCode) {
      setValue('contactPhone', COUNTRY_DIAL_CODES[phoneCountry]);
    }
  }, [warehouses, isLoadingWarehouses, profile?.data?.country]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'requiresHomePickup') {
        const newRequiresHomePickup = value.requiresHomePickup || false;

        if (!newRequiresHomePickup && currentStep === 4) {
          setCurrentStep(4);
        }

        if (!newRequiresHomePickup) {
          setIsAddressConfirmed(false);
          setIsAddressTouched(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, currentStep, editStep, setIsAddressConfirmed, setIsAddressTouched]);

  const onChangeProducts = useCallback(
    ({ index, sku, quantity }: EditProducts) => {
      const quantityParsed = parseInt(quantity.trim()) ?? null;
      setValue(`products.${index}.sku`, sku);
      setValue(`products.${index}.quantity`, quantityParsed);
      trigger('products');
    },
    [setValue, trigger],
  );

  const onWarehouseChange = useCallback(
    (warehouseValue: string) => {
      setWarehouse(warehouseValue);
      setValue('warehouse', warehouseValue);
      trigger('warehouse');
    },
    [setValue, trigger],
  );

  const createArrivalOrder = useCallback(
    (data: SendToWarehouseFormData) => {
      const { firstName } = profile.data?.profile ?? { firstName: '' };
      startTransition(async () => {
        try {
          const basePayload: IReqCreateArrival = {
            products: data.products,
            parcels: data.parcels,
            id: '',
            appointment: data.appointment
              ? new Date(data.appointment).toISOString()
              : new Date().toISOString(),
            warehouseId: data.warehouse,
            contact: {
              name: firstName,
              address: profile?.data?.profile.address ?? '',
              phone: data.contactPhone,
            },
            pickupRequired: data.requiresHomePickup,
          };

          if (data.requiresHomePickup && data.pickup) {
            const packageSizeData = PACKAGE_SIZES.find(
              (size) => size.value === data.pickup.packageSize,
            );

            const weight = packageSizeData?.grams;

            basePayload.pickupDetails = {
              packageSize: data.pickup.packageSize,
              shipmentType: data.shipmentType === 'SPT' ? 'NXD' : data.shipmentType,
              origin: {
                address: data.pickup.address,
                city: data.pickup.city,
                reference: data.pickup.reference || '',
                phone: data.contactPhone,
                zipcode: data.pickup.postalCode,
                country: countryInfo.code,
              },
              weight: weight || 6000, // Set M as the default weight if package size is not found
              notes: data.pickup.notes || '',
            };
          }

          const response = await http.inventory.createArrival(basePayload);

          if (response.data?.warnings && response.data.warnings.length > 0) {
            const firstWarning = response.data.warnings[0];
            const requestId = firstWarning.request_id || firstWarning.details?.request_id;
            const translatedMessage = translateErrorMessage(
              firstWarning.message,
              'Se creó la orden con advertencias.',
            );

            toast.warning(
              <div>
                <div>{translatedMessage}</div>
                {firstWarning.details?.affectedPickups?.length > 0 && (
                  <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                    {firstWarning.details.affectedPickups.length} recolección(es) afectada(s)
                  </div>
                )}
                {requestId && (
                  <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                    ID: {requestId}
                  </div>
                )}
              </div>,
              { autoClose: 7000 },
            );
          } else {
            toast.success('Se ha programado el envío correctamente');
          }

          onClose();
        } catch (error: unknown) {
          console.error('Error al programar el envío', error);

          const { message: errorMessage, requestId } = extractErrorInfo(
            error,
            'Error al programar el envío',
          );

          const translatedMessage = translateErrorMessage(
            errorMessage,
            'Error al programar el envío. Por favor, intenta nuevamente.',
          );

          toast.error(
            <div>
              <div>{translatedMessage}</div>
              {requestId && (
                <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                  ID: {requestId}
                </div>
              )}
            </div>,
            { autoClose: 7000 },
          );
        }
      });
    },
    [profile, onClose, startTransition, countryInfo.code],
  );

  const onError = useCallback((errors: FieldErrors) => {
    console.error('Errores de validación del formulario:', errors);
    toast.error('Por favor, complete todos los campos requeridos');
  }, []);

  const isDisabled = Boolean(
    'products' in errors ||
    getValues('products') === undefined ||
    getValues('products')?.some((item) => !item.sku || !item.quantity) ||
    getValues('products')?.length !== products.length ||
    !warehouse,
  );

  const requiresHomePickup = watch('requiresHomePickup');

  const isStepDisabled = (currentStep: number) => {
    const pickupData = watch('pickup') as PickupData | undefined;

    switch (currentStep) {
      case 1:
        return Boolean(
          errors.products ||
          !getValues('products') ||
          getValues('products')?.some((item) => !item.sku || !item.quantity) ||
          getValues('products')?.length !== products.length ||
          !warehouse,
        );

      case 2:
        return Boolean(errors.appointment || !watch('appointment'));

      case 3: {
        const phone = (watch('contactPhone') || '').replace(/\s/g, '');
        const requiredPhoneLength = MAX_PHONE_LENGTH_BY_COUNTRY[countryCode] || 13;
        return Boolean(
          errors.parcels ||
          !watch('parcels') ||
          errors.contactPhone ||
          phone.length < requiredPhoneLength ||
          errors.shipmentType ||
          !watch('shipmentType'),
        );
      }

      case 4: {
        if (!requiresHomePickup) return false;

        const selectedCountry = pickupData?.country || '';
        const requiresPostalCode = pickupCountryRequiresZipcode(selectedCountry);

        return Boolean(
          !pickupData?.packageSize ||
          !pickupData?.address ||
          !pickupData?.city ||
          !isAddressConfirmed ||
          (requiresPostalCode && !pickupData?.postalCode) ||
          errors.pickup,
        );
      }

      default:
        return false;
    }
  };
  const goEdit = (step: number) => {
    setEditStep(true);
    setCurrentStep(step);
  };

  const totalQuantity =
    getValues('products')?.reduce((sum, product) => {
      return sum + (product.quantity || 0);
    }, 0) ?? 0;

  const totalProductTypes = getValues('products')?.length ?? 0;

  const handleChangeSelectedCountryPhone = useCallback(
    (country: CountryCode) => {
      setCountryCode(country);
      trigger('contactPhone');
    },
    [trigger],
  );

  const totalVolume =
    getValues('products')?.reduce((acc, product) => {
      const productInfo = products.find((p) => p.sku === product.sku);
      const quantity = product.quantity || 0;
      if (productInfo?.dimensions) {
        // Assumption: length, width, and height are in centimeters (cm).
        // The calculated volume is in cubic centimeters (cm³).
        const { length, width, height } = productInfo.dimensions;
        const volumeInCm3 = length * width * height * quantity;
        return acc + volumeInCm3;
      }
      return acc;
    }, 0) ?? 0;

  const totalVolumeInCm3 = totalVolume.toFixed(0);

  const pickupData = watch('pickup') as PickupData | undefined;

  const drawerData = [
    {
      step: 1,
      title: `Estás por enviar ${totalProductTypes} producto${totalProductTypes === 1 ? '' : 's'}`,
      description: (
        <Box>
          <Typography variant="body1">
            <strong>Volumen estimado:</strong> {totalVolumeInCm3} cm³
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
            Total de unidades: <strong>{totalQuantity}</strong>
          </Typography>
        </Box>
      ),
      goEdit: goEdit,
    },
    {
      step: 2,
      title: `Fecha de envío: ${getValues('appointment') ? formatDate(getValues('appointment')!.toISOString(), 'dd/MM/yyyy') : ''}`,
      description: requiresHomePickup
        ? 'Se programará una recolección a domicilio'
        : 'Ese mismo día te enviaremos el detalle para tu ingreso',
      goEdit: goEdit,
    },
    {
      step: 3,
      title: 'Etiqueta tus productos y bultos',
      description: (
        <Box>
          <Typography variant="body2">
            <strong>Bultos:</strong> {getValues('parcels') || ''}
          </Typography>
          <Typography variant="body2">
            <strong>Tipo de envío:</strong> {watch('shipmentType') || 'SPT'}
          </Typography>
        </Box>
      ),
      goEdit: goEdit,
    },
    ...(requiresHomePickup
      ? [
          {
            step: 4,
            title: 'Datos de recolección',
            description: (
              <Box>
                <Typography variant="body2">
                  <strong>Tamaño:</strong> {pickupData?.packageSize || ''}
                </Typography>
                <Typography variant="body2">
                  <strong>Dirección:</strong> {pickupData?.address || ''}
                </Typography>
              </Box>
            ),
            goEdit: goEdit,
          },
        ]
      : []),
  ];

  return {
    register,
    handleSubmit,
    errors,
    touchedFields,
    onChangeProducts,
    onWarehouseChange,
    warehouse,
    warehouseOptions,
    isLoadingWarehouses,
    control,
    trigger,
    isPending,
    createArrivalOrder,
    onError,
    isDisabled,
    getValues,
    drawerData,
    editStep,
    currentStep,
    setCurrentStep,
    isStepDisabled,
    handleChangeSelectedCountryPhone,
    selectedWarehouse,
    requiresHomePickup,
    watch,
    setValue,
    isAddressConfirmed,
    setIsAddressConfirmed,
    isAddressTouched,
    setIsAddressTouched,
    userCountry: countryInfo.display,
    userCountryCode: countryInfo.code,
    countryCode,
  } as const;
};

export default useSendToWarehouse;
